/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PackageSelectItemDto } from '../models/package-select-item-dto';
import { PagePackageSortimentResponse } from '../models/page-package-sortiment-response';

/**
 * Package sortiment resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class PackageSortimentsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getPackageSortiment
   */
  static readonly GetPackageSortimentPath = '/sortiments/packages';

  /**
   * Gets packages sortiment.
   *
   * Finds and returns package sortiment of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackageSortiment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageSortiment$Response(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on mainType of package
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of package
       */
      subType?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagePackageSortimentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackageSortimentsService.GetPackageSortimentPath,
      'get'
    );
    if (params) {
      rb.query('name', params.name, {});
      rb.query('mainType', params.mainType, {});
      rb.query('subType', params.subType, {});
      rb.query('unit', params.unit, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagePackageSortimentResponse>;
        })
      );
  }

  /**
   * Gets packages sortiment.
   *
   * Finds and returns package sortiment of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackageSortiment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageSortiment(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on mainType of package
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of package
       */
      subType?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagePackageSortimentResponse> {
    return this.getPackageSortiment$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagePackageSortimentResponse>) =>
          r.body as PagePackageSortimentResponse
      )
    );
  }

  /**
   * Path part for operation getPackageSelectItemsFromSortiment
   */
  static readonly GetPackageSelectItemsFromSortimentPath =
    '/sortiments/packages/select-items';

  /**
   * Gets package dropdown select items from users sortiment.
   *
   * Finds and returns package select items from sortiment of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackageSelectItemsFromSortiment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageSelectItemsFromSortiment$Response(
    params: {
      subType: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<PackageSelectItemDto>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackageSortimentsService.GetPackageSelectItemsFromSortimentPath,
      'get'
    );
    if (params) {
      rb.query('subType', params.subType, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<PackageSelectItemDto>>;
        })
      );
  }

  /**
   * Gets package dropdown select items from users sortiment.
   *
   * Finds and returns package select items from sortiment of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackageSelectItemsFromSortiment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageSelectItemsFromSortiment(
    params: {
      subType: string;
    },
    context?: HttpContext
  ): Observable<Array<PackageSelectItemDto>> {
    return this.getPackageSelectItemsFromSortiment$Response(
      params,
      context
    ).pipe(
      map(
        (r: StrictHttpResponse<Array<PackageSelectItemDto>>) =>
          r.body as Array<PackageSelectItemDto>
      )
    );
  }
}
